/**
 * Notification preferences form helpers (Methods + Specifics).
 * Shape matches docs/notification-prefs-granularity.md GraphQL contract.
 */

export type RoleBools = {
  assignee: boolean;
  owner: boolean;
  include: boolean;
};

export type PartialRoleBools = {
  assignee?: boolean;
  owner?: boolean;
  include?: boolean;
};

export type NotificationMethods = {
  messages: { email: boolean; sms: boolean };
  jobReminders: { email: boolean; sms: boolean };
  jobSummaries: { email: boolean };
  statusChanges: { email: boolean };
};

export type Last24hStatusKey =
  | 'created'
  | 'offered'
  | 'accepted'
  | 'declined'
  | 'confirmed'
  | 'started'
  | 'completed'
  | 'cancelled';

export type StatusChangesKey =
  | 'offered'
  | 'accepted'
  | 'declined'
  | 'confirmed'
  | 'started'
  | 'completed'
  | 'cancelled';

export type NotificationSpecifics = {
  jobReminders: {
    twentyFourHoursBefore: RoleBools;
    oneHourBefore: RoleBools;
    oneHourAfter: RoleBools;
    twentyFourHoursAfter: RoleBools;
  };
  jobSummaries: {
    next24Hours: RoleBools & { sendTime: string };
    last24Hours: RoleBools & {
      sendTime: string;
      statusChangesToInclude: Record<Last24hStatusKey, RoleBools>;
    };
    sendTimeZone: string;
  };
  statusChanges: {
    offered: PartialRoleBools;
    accepted: PartialRoleBools;
    declined: PartialRoleBools;
    confirmed: PartialRoleBools;
    started: PartialRoleBools;
    completed: PartialRoleBools;
    cancelled: PartialRoleBools;
  };
};

export type UserNotificationSettingsV2 = {
  methods: NotificationMethods;
  specifics: NotificationSpecifics;
};

export type NotificationRole = keyof RoleBools;

/** Writable Status Changes cells (blank = never send / no UI control). */
export const STATUS_CHANGES_WRITABLE: Record<
  StatusChangesKey,
  ReadonlyArray<NotificationRole>
> = {
  offered: ['assignee', 'include'],
  accepted: ['owner', 'include'],
  declined: ['owner', 'include'],
  confirmed: ['assignee', 'include'],
  started: ['owner', 'include'],
  completed: ['owner', 'include'],
  cancelled: ['assignee', 'include'],
};

export const STATUS_CHANGES_ROWS: ReadonlyArray<{
  key: StatusChangesKey;
  label: string;
}> = [
  { key: 'offered', label: 'Offered' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'declined', label: 'Declined' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'started', label: 'Started' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

export const LAST24H_STATUS_ROWS: ReadonlyArray<{
  key: Last24hStatusKey;
  label: string;
  hint?: string;
}> = [
  { key: 'created', label: 'Created', hint: 'including via automations' },
  { key: 'offered', label: 'Offered' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'declined', label: 'Declined' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'started', label: 'Started' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

export const REMINDER_TIMING_ROWS: ReadonlyArray<{
  key: keyof NotificationSpecifics['jobReminders'];
  label: string;
}> = [
  { key: 'twentyFourHoursBefore', label: '24 Hours Before' },
  { key: 'oneHourBefore', label: '1 Hour Before' },
  { key: 'oneHourAfter', label: '1 Hour After (if incomplete)' },
  { key: 'twentyFourHoursAfter', label: '24 Hours After (if incomplete)' },
];

export const ROLE_COLUMNS: ReadonlyArray<{
  key: NotificationRole;
  label: string;
}> = [
  { key: 'assignee', label: "Jobs I'm Assigned To" },
  { key: 'owner', label: "Jobs Where I'm the Job Owner" },
  { key: 'include', label: "Jobs I'm Included On" },
];

export const DEFAULT_SEND_TIME = '18:00';

export function allRoles(value: boolean): RoleBools {
  return { assignee: value, owner: value, include: value };
}

export function emptyLast24hStatusInclude(
  value: boolean
): Record<Last24hStatusKey, RoleBools> {
  return {
    created: allRoles(value),
    offered: allRoles(value),
    accepted: allRoles(value),
    declined: allRoles(value),
    confirmed: allRoles(value),
    started: allRoles(value),
    completed: allRoles(value),
    cancelled: allRoles(value),
  };
}

export function emptyStatusChangesSpecifics(
  value: boolean
): NotificationSpecifics['statusChanges'] {
  const cell = (roles: ReadonlyArray<NotificationRole>): PartialRoleBools => {
    const out: PartialRoleBools = {};
    for (const role of roles) {
      out[role] = value;
    }
    return out;
  };
  return {
    offered: cell(STATUS_CHANGES_WRITABLE.offered),
    accepted: cell(STATUS_CHANGES_WRITABLE.accepted),
    declined: cell(STATUS_CHANGES_WRITABLE.declined),
    confirmed: cell(STATUS_CHANGES_WRITABLE.confirmed),
    started: cell(STATUS_CHANGES_WRITABLE.started),
    completed: cell(STATUS_CHANGES_WRITABLE.completed),
    cancelled: cell(STATUS_CHANGES_WRITABLE.cancelled),
  };
}

export function getBrowserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** New-user opt-down defaults (all writable cells on). */
export function createDefaultNotificationSettings(
  sendTimeZone: string = getBrowserTimeZone()
): UserNotificationSettingsV2 {
  return {
    methods: {
      messages: { email: true, sms: true },
      jobReminders: { email: true, sms: true },
      jobSummaries: { email: true },
      statusChanges: { email: true },
    },
    specifics: {
      jobReminders: {
        twentyFourHoursBefore: allRoles(true),
        oneHourBefore: allRoles(true),
        oneHourAfter: allRoles(true),
        twentyFourHoursAfter: allRoles(true),
      },
      jobSummaries: {
        next24Hours: { ...allRoles(true), sendTime: DEFAULT_SEND_TIME },
        last24Hours: {
          ...allRoles(true),
          sendTime: DEFAULT_SEND_TIME,
          statusChangesToInclude: emptyLast24hStatusInclude(true),
        },
        sendTimeZone,
      },
      statusChanges: emptyStatusChangesSpecifics(true),
    },
  };
}

/** Existing-user Specifics backfill (all off) used when normalizing legacy payloads. */
export function createExistingUserSpecifics(
  sendTimeZone: string = getBrowserTimeZone()
): NotificationSpecifics {
  return {
    jobReminders: {
      twentyFourHoursBefore: allRoles(false),
      oneHourBefore: allRoles(false),
      oneHourAfter: allRoles(false),
      twentyFourHoursAfter: allRoles(false),
    },
    jobSummaries: {
      next24Hours: { ...allRoles(false), sendTime: DEFAULT_SEND_TIME },
      last24Hours: {
        ...allRoles(false),
        sendTime: DEFAULT_SEND_TIME,
        statusChangesToInclude: emptyLast24hStatusInclude(false),
      },
      sendTimeZone,
    },
    statusChanges: emptyStatusChangesSpecifics(false),
  };
}

type LegacyNotificationSettings = {
  messages?: { email?: boolean; sms?: boolean };
  statusChanges?: { email?: boolean; sms?: boolean };
  jobReminders?: {
    twentyFourHoursPrior?: { email?: boolean; sms?: boolean };
    oneHourPrior?: { email?: boolean; sms?: boolean };
  };
  methods?: NotificationMethods;
  specifics?: NotificationSpecifics;
};

function isV2Shape(
  raw: LegacyNotificationSettings
): raw is UserNotificationSettingsV2 {
  return Boolean(raw?.methods && raw?.specifics);
}

/**
 * Normalize session/API payload into Methods + Specifics.
 * Legacy main shape (no methods/specifics) maps per migration rules:
 * Methods preserved / OR'd; Specifics all off.
 */
export function normalizeNotificationSettings(
  raw: LegacyNotificationSettings | null | undefined,
  sendTimeZone: string = getBrowserTimeZone()
): UserNotificationSettingsV2 {
  if (raw && isV2Shape(raw)) {
    return {
      methods: {
        messages: {
          email: Boolean(raw.methods.messages?.email),
          sms: Boolean(raw.methods.messages?.sms),
        },
        jobReminders: {
          email: Boolean(raw.methods.jobReminders?.email),
          sms: Boolean(raw.methods.jobReminders?.sms),
        },
        jobSummaries: {
          email: Boolean(raw.methods.jobSummaries?.email),
        },
        statusChanges: {
          email: Boolean(raw.methods.statusChanges?.email),
        },
      },
      specifics: mergeSpecifics(raw.specifics, sendTimeZone),
    };
  }

  const messages = {
    email: Boolean(raw?.messages?.email),
    sms: Boolean(raw?.messages?.sms),
  };
  // Job Messages ≥1 channel
  if (!messages.email && !messages.sms) {
    messages.email = true;
  }

  const prior24 = raw?.jobReminders?.twentyFourHoursPrior;
  const prior1 = raw?.jobReminders?.oneHourPrior;

  return {
    methods: {
      messages,
      jobReminders: {
        email: Boolean(prior24?.email || prior1?.email),
        sms: Boolean(prior24?.sms || prior1?.sms),
      },
      jobSummaries: { email: false },
      statusChanges: {
        email: Boolean(raw?.statusChanges?.email),
      },
    },
    specifics: createExistingUserSpecifics(sendTimeZone),
  };
}

function mergeSpecifics(
  specifics: NotificationSpecifics,
  sendTimeZone: string
): NotificationSpecifics {
  const defaults = createExistingUserSpecifics(sendTimeZone);
  return {
    jobReminders: {
      twentyFourHoursBefore: {
        ...defaults.jobReminders.twentyFourHoursBefore,
        ...specifics.jobReminders?.twentyFourHoursBefore,
      },
      oneHourBefore: {
        ...defaults.jobReminders.oneHourBefore,
        ...specifics.jobReminders?.oneHourBefore,
      },
      oneHourAfter: {
        ...defaults.jobReminders.oneHourAfter,
        ...specifics.jobReminders?.oneHourAfter,
      },
      twentyFourHoursAfter: {
        ...defaults.jobReminders.twentyFourHoursAfter,
        ...specifics.jobReminders?.twentyFourHoursAfter,
      },
    },
    jobSummaries: {
      next24Hours: {
        ...defaults.jobSummaries.next24Hours,
        ...specifics.jobSummaries?.next24Hours,
        sendTime:
          specifics.jobSummaries?.next24Hours?.sendTime ?? DEFAULT_SEND_TIME,
      },
      last24Hours: {
        ...defaults.jobSummaries.last24Hours,
        ...specifics.jobSummaries?.last24Hours,
        sendTime:
          specifics.jobSummaries?.last24Hours?.sendTime ?? DEFAULT_SEND_TIME,
        statusChangesToInclude: {
          ...defaults.jobSummaries.last24Hours.statusChangesToInclude,
          ...specifics.jobSummaries?.last24Hours?.statusChangesToInclude,
        },
      },
      sendTimeZone: specifics.jobSummaries?.sendTimeZone || sendTimeZone,
    },
    statusChanges: mergeStatusChangesSpecifics(specifics.statusChanges),
  };
}

function mergeStatusChangesSpecifics(
  incoming: NotificationSpecifics['statusChanges'] | undefined
): NotificationSpecifics['statusChanges'] {
  const base = emptyStatusChangesSpecifics(false);
  if (!incoming) {
    return base;
  }
  const out = { ...base };
  for (const row of STATUS_CHANGES_ROWS) {
    const roles = STATUS_CHANGES_WRITABLE[row.key];
    const merged: PartialRoleBools = {};
    for (const role of roles) {
      merged[role] = Boolean(
        incoming[row.key]?.[role] ?? base[row.key]?.[role]
      );
    }
    out[row.key] = merged;
  }
  return out;
}

export type NotificationPrefsVisibility = {
  jobRemindersSpecifics: boolean;
  jobSummariesSpecifics: boolean;
  last24hStatusInclude: boolean;
  statusChangesSpecifics: boolean;
};

export function getNotificationPrefsVisibility(
  methods: NotificationMethods,
  _last24Hours?: RoleBools
): NotificationPrefsVisibility {
  return {
    jobRemindersSpecifics: Boolean(
      methods.jobReminders.email || methods.jobReminders.sms
    ),
    jobSummariesSpecifics: Boolean(methods.jobSummaries.email),
    // Status-changes-to-include removed: Last 24h shows all changes when
    // the parent Last 24h role is on (Job Changes digest).
    last24hStatusInclude: false,
    statusChangesSpecifics: Boolean(methods.statusChanges.email),
  };
}

/**
 * Enforce Job Messages ≥1 of Email/SMS.
 * If clearing the last channel, keep/re-check the other.
 */
export function nextMessagesChannels(
  current: { email: boolean; sms: boolean },
  channel: 'email' | 'sms',
  nextValue: boolean
): { email: boolean; sms: boolean } {
  const next = { ...current, [channel]: nextValue };
  if (!next.email && !next.sms) {
    if (channel === 'email') {
      return { email: false, sms: true };
    }
    return { email: true, sms: false };
  }
  return next;
}

export function isStatusChangesCellWritable(
  status: StatusChangesKey,
  role: NotificationRole
): boolean {
  return STATUS_CHANGES_WRITABLE[status].includes(role);
}

/** HH:mm options every 30 minutes (00:00–23:30). */
export function buildSendTimeOptions(
  stepMinutes = 30
): ReadonlyArray<{ value: string; label: string }> {
  const options: { value: string; label: string }[] = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += stepMinutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    options.push({ value, label: formatSendTimeLabel(value) });
  }
  return options;
}

export function formatSendTimeLabel(hhmm: string): string {
  const [hStr, mStr] = hhmm.split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  if (!Number.isFinite(h) || !Number.isFinite(m)) {
    return hhmm;
  }
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

export function isValidSendTime(hhmm: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(hhmm);
}
